/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from '../api/base'

interface FilterType {
  webSocketHistoryType: string[]
  tenantUuid?: string
}

interface MetaDataType {
  pageSize: number
  pageStart: number
  sortBy: string
  sortDesc: boolean
}

interface UserType {
  chName: string
  email: string
  enabled: boolean
  firstName: string
  fullName: string
  id: number
  lastName: string
  name: string
  role: string
  systemHelperType: string
  _uid: string
}

interface ToUserType {
  merged: UserType[]
}

interface SendEmailReqObjtype {
  userIds: number[]
  groupIds: number[]
  subject: string
  body: string
  expiration?: number
}

interface SettingType {
  enabled: boolean
  notificationType: string
  readOnly: boolean
}

interface SettingsDataType {
  messageType: string
  setting: SettingType[]
}

interface SettingsType {
  availableSettings: SettingsDataType[]
}

interface ReturnedSettingType {
  messageType: string
  value: string
  notification: {
    show?: boolean
    enabled?: boolean
    readOnly?: boolean
  }
  email: {
    show?: boolean
    enabled?: boolean
    readOnly?: boolean
  }
}

interface SaveSettingType {
  messageType: string
  newState: boolean
  type: string
}

interface SaveSettingsRequestType {
  settings: SaveSettingType[]
}

const notificationTypes = {
  TASKS: 'NOTIFICATION_TYPE_TASKS',
  CHANGE_REQUEST: 'NOTIFICATION_TYPE_CHANGE_REQUEST',
  UPLOAD_FILES: 'NOTIFICATION_TYPE_UPLOAD_FILES',
  DOC_MIGRATIONS_EXPORT: 'NOTIFICATION_TYPE_DOC_MIGRATIONS_EXPORT',
  FOLDER_STRUCTURE: 'NOTIFICATION_TYPE_FOLDER_STRUCTURE',
  REPORT_EXPORT: 'NOTIFICATION_TYPE_REPORT_EXPORT',
  USER_ACCOUNTS_EXPIRATION: 'NOTIFICATION_TYPE_USER_ACCOUNTS_EXPIRATION',
  PERIODIC_REVIEW: 'NOTIFICATION_TYPE_PERIODIC_REVIEW',
  SETTINGS_SYSTEM_EVENTS: 'NOTIFICATION_TYPE_SETTINGS_SYSTEM_EVENTS',
  DEVICE_VERIFICATION: 'NOTIFICATION_TYPE_DEVICE_VERIFICATION',
  USER_ACCOUNT_LOCKED: 'NOTIFICATION_TYPE_USER_ACCOUNT_LOCKED',
  // USE REACT init() SERVICE HERE
  // ...(initService.getFeaturesInfo() && initService.getFeaturesInfo().isEnableDocDetailsExpirationDate ? { DOCUMENT_EXPIRATION: 'NOTIFICATION_TYPE_DOCUMENT_EXPIRATION') } : {}),
  ETMF_EVENT: 'NOTIFICATION_TYPE_ETMF_EVENT',
  EOS: 'NOTIFICATION_TYPE_EOS',
  DOCUMENTS_REJECTION: 'NOTIFICATION_TYPE_DOC_REJECTION'
}

export const countAll = () => {
  return baseApiService.fetch('/api/websocket/countall')
}

export const getAllFiltered = (filter: FilterType, meta: MetaDataType) => {
  return baseApiService.fetch('/api/websocket/history', {
    method: 'POST',
    body: {
      filter,
      meta
    }
  })
}

export const send = (to: ToUserType, subject: string, body: string, expiration?: Date) => {
  const requestObj: any = {
    userIds: [],
    groupIds: [],
    subject: subject,
    body: body
  }
  if (to?.merged?.length > 0) {
    for (const key4 in to.merged) {
      if (to.merged[key4].systemHelperType === 'USER') {
        requestObj.userIds.push(to.merged[key4].id || 0)
      } else if (to.merged[key4].systemHelperType === 'GROUP') {
        requestObj.groupIds.push(to.merged[key4].id)
      }
    }
  }

  // Only include expiration if a valid date is provided
  if (expiration instanceof Date) {
    requestObj.expiration = expiration.getTime()
  }

  return baseApiService.fetch('/api/websocket/adminMessageUsersGroups', {
    method: 'POST',
    body: {
      ...requestObj
    }
  })
}

export const remove = (notificationIds: number | number[]) => {
  const requestObj = { messageIds: Array.isArray(notificationIds) ? notificationIds : [notificationIds] }

  return baseApiService.fetch('/api/websocket/delete', {
    method: 'POST',
    body: {
      ...requestObj
    }
  })
}

export const removeAll = (filter: FilterType) => {
  return baseApiService.fetch('/api/websocket/deleteall', {
    method: 'POST',
    body: {
      ...filter
    }
  })
}

export const getWsHistoryTypes = () => {
  return baseApiService.fetch('/api/websocket/filter')
}

export const getSettings = () => {
  const returningSettings: ReturnedSettingType[] = []

  return new Promise((resolve, reject) => {
    baseApiService.fetch('/api/websocket/settings').then(
      (respData) => {
        const data = respData as SettingsType
        const tmpData = data.availableSettings
        tmpData.forEach((item) => {
          if (notificationTypes[(item?.messageType ?? '') as keyof typeof notificationTypes]) {
            const tmpObj = {
              messageType: item.messageType,
              value: notificationTypes[item.messageType as keyof typeof notificationTypes],
              notification: { show: false, enabled: false, readOnly: false },
              email: { show: false, enabled: false, readOnly: false }
            }
            item.setting.forEach((setting) => {
              if (setting.notificationType === 'NOTIFICATION') {
                tmpObj.notification.show = true
                tmpObj.notification.enabled = setting.enabled
                tmpObj.notification.readOnly = setting.readOnly
              } else if (setting.notificationType === 'EMAIL') {
                tmpObj.email.show = true
                tmpObj.email.enabled = setting.enabled
                tmpObj.email.readOnly = setting.readOnly
              }
            })
            returningSettings.push(tmpObj)
          }
        })

        resolve(returningSettings)
      },
      (error) => {
        console.error(error)
        reject(error)
      }
    )
  })
}

export const saveSettings = (data: ReturnedSettingType[]) => {
  const requestObj: SaveSettingsRequestType = { settings: [] }

  data.forEach(function (item) {
    if (item.notification) {
      requestObj.settings.push({
        messageType: item.messageType,
        newState: !!item.notification.enabled,
        type: 'NOTIFICATION'
      })
    }
    if (item.email) {
      requestObj.settings.push({
        messageType: item.messageType,
        newState: !!item.email.enabled,
        type: 'EMAIL'
      })
    }
  })

  return baseApiService.fetch('/api/websocket/changeNotificationSettings', {
    method: 'POST',
    body: { ...requestObj }
  })
}

export const markAsViewedAll = (filter: FilterType) => {
  return baseApiService.fetch('/api/websocket/seenall', {
    method: 'POST',
    body: { ...filter }
  })
}

export const markAsUnviewedAll = (filter: FilterType) => {
  return baseApiService.fetch('/api/websocket/unseenall', {
    method: 'POST',
    body: { ...filter }
  })
}

export const markAsViewed = (notificationIds: number | number[]) => {
  const requestObj = { messageIds: Array.isArray(notificationIds) ? notificationIds : [notificationIds], seen: true }
  return baseApiService.fetch('/api/websocket/seen', {
    method: 'POST',
    body: { ...requestObj }
  })
}

export const markAsUnviewed = (notificationIds: number | number[]) => {
  const requestObj = { messageIds: Array.isArray(notificationIds) ? notificationIds : [notificationIds], seen: false }
  return baseApiService.fetch('/api/websocket/seen', {
    method: 'POST',
    body: { ...requestObj }
  })
}

export const getCountNotSeen = () => {
  return baseApiService.fetch('/api/websocket/countNotSeen')
}

export const useNotificationsFactory = () => {
  return {
    countAll,
    getAllFiltered,
    getCountNotSeen,
    getSettings,
    getWsHistoryTypes,
    markAsUnviewed,
    markAsUnviewedAll,
    markAsViewed,
    markAsViewedAll,
    remove,
    removeAll,
    saveSettings,
    send
  }
}
